import { createFeatureSelector, createSelector } from "@ngrx/store";
import { BookingState } from "./booking.state";
import {
  PackageResponse,
  PackageTagEnum,
  PackageTypeEnum,
  PropertyEnum,
  SelectedPackagesRequest,
  WorkshopSystemEnum
} from "src/domain/client";
import {
  systemToFormNameMap,
  systemToPackageTypeMap
} from "./utils/booking-selector.helpter";
import { requiredCharacters, SelectedPackages } from "./utils/booking.modals";
import { _getWorkshopHoursForDate } from "./utils/booking-effects.helper";

// --- Shared  ---
export const selectBookingState =
  createFeatureSelector<BookingState>("booking");

export const selectIsLoading = createSelector(
  selectBookingState,
  (state: BookingState) => state.isLoading
);

export const selectIsPartialLoading = createSelector(
  selectBookingState,
  (state) => state.isPartialLoading
);

export const selectError = createSelector(
  selectBookingState,
  (state: BookingState) => state.error
);

export const selectIsBusinessProposalActive = createSelector(
  selectBookingState,
  (state: BookingState) => state.isBusinessProposalActive
);

export const selectFileIds = createSelector(
  selectBookingState,
  (state: BookingState) => state.fileIds
);

// --- Step 1  ---
export const selectCarInfo = createSelector(
  selectBookingState,
  (state: BookingState) => state.carInfo
);

export const selectOdometer = createSelector(
  selectBookingState,
  (state: BookingState) => state.odometer
);

export const selectProviderIds = createSelector(
  selectBookingState,
  (state: BookingState) => state.providerIds
);

export const selectTechUpdateStatus = createSelector(
  selectBookingState,
  (state: BookingState) => state.techUpdateStatus
);

// --- Step 2  ---
export const selectActiveWorkshopSystems = createSelector(
  selectBookingState,
  (state: BookingState) => state.activeWorkshopSystems
);

export const selectSortedActiveWorkshopSystems = createSelector(
  selectActiveWorkshopSystems,
  (systems) => {
    if (!systems) {
      return null;
    }
    const techUpdate = systems.find(
      (s) => s.workshopSystemId === WorkshopSystemEnum.TechUpdate
    );
    if (techUpdate) {
      return [
        techUpdate,
        ...systems.filter(
          (s) => s.workshopSystemId !== WorkshopSystemEnum.TechUpdate
        )
      ];
    }
    return systems;
  }
);

export const selectSelectedWorkshopSystems = createSelector(
  selectBookingState,
  (state: BookingState) => state.selectedWorkshopSystems
);

// --- Step 3  ---
export const selectDealers = createSelector(
  selectBookingState,
  (state: BookingState) => state.dealers
);

export const selectSelectedDealerNumber = createSelector(
  selectBookingState,
  (state: BookingState) => state.selectedDealerNumber
);

export const selectSelectedDealer = createSelector(
  selectDealers,
  selectSelectedDealerNumber,
  (dealers, selectedNumber) => {
    if (!dealers || !selectedNumber) {
      return null;
    }
    return dealers.find((d) => d.number === selectedNumber) ?? null;
  }
);

export const selectEnabledServiceTypes = createSelector(
  selectBookingState,
  (state: BookingState) => state.enabledServiceTypes
);

// --- Step 4  ---
export const selectBasePackages = createSelector(
  selectBookingState,
  (state) => state.basePackages
);

export const selectAdditionalPackages = createSelector(
  selectBookingState,
  (state) => state.additionalPackages
);

export const selectInsuranceProviders = createSelector(
  selectBookingState,
  (state) => state.insuranceProviders
);

export const selectWarningLights = createSelector(
  selectBookingState,
  (state) => state.warningLights
);

export const selectExternalTyreUrl = createSelector(
  selectBookingState,
  (state) => state.externalTyreUrl
);
export const selectVehicleSegment = createSelector(
  selectBookingState,
  (state) => state.vehicleSegment
);

export const selectSelectedPackages = createSelector(
  selectBookingState,
  (state) => state.selectedPackages
);

export const selectWarningLightIds = createSelector(
  selectSelectedPackages,
  (selectedPackages): number[] => {
    const icons = selectedPackages?.troubleshootingForm?.icons ?? {};
    return Object.entries(icons)
      .filter(([key, value]) => value === true)
      .map(([key, value]) => parseInt(key));
  }
);

export const selectSelectedWarningLights = createSelector(
  selectWarningLights,
  selectWarningLightIds,
  (warningLights, selectedIds) => {
    if (!warningLights || !selectedIds.length) return [];
    return warningLights.filter((light) =>
      selectedIds.includes(light.warningLightId)
    );
  }
);

export const selectInsuranceProviderName = createSelector(
  selectInsuranceProviders,
  selectSelectedPackages,
  (insuranceProviders, selectedPackages): string | null => {
    try {
      const insuranceId = selectedPackages?.windscreenForm?.insurance;
      if (!insuranceId || !insuranceProviders) return null;

      const provider = insuranceProviders.find(
        (p) => p.insuranceCompanyId.toString() === insuranceId
      );
      return provider?.name ?? null;
    } catch {
      return null;
    }
  }
);

export const selectProposalCustomer = createSelector(
  selectBookingState,
  (state) => state.proposalCustomer
);

export const selectServiceAdditionalPackagesStep4 = createSelector(
  selectAdditionalPackages,
  (packages) =>
    (packages ?? []).filter((p) =>
      [PackageTypeEnum.ServiceAdditional, PackageTypeEnum.PPS].includes(p.type)
    )
);

// --- Step 5  ---
export const selectServiceAdditionalPackagesStep5 = createSelector(
  selectAdditionalPackages,
  (packages) =>
    (packages ?? []).filter(
      (p) =>
        p.type === PackageTypeEnum.WashAndRecond ||
        p.type === PackageTypeEnum.DealerExtra
    )
);

// --- Step 6  ---
export const selectAvailableTimes = createSelector(
  selectBookingState,
  (state) => state.availableTimes
);
export const selectSelectedDate = createSelector(
  selectBookingState,
  (state) => state.selectedDate
);
export const selectSelectedTime = createSelector(
  selectBookingState,
  (state) => state.selectedTime
);

export const selectedWorkshopDate = createSelector(
  selectAvailableTimes,
  selectSelectedDate,
  (times, selectedDate) => {
    if (!times || !selectedDate) return null;
    const date = new Date(selectedDate);
    return (
      times.find(
        (d) => new Date(d.date).toDateString() === date.toDateString()
      ) ?? null
    );
  }
);
export const selectSelectedDayAvailableHours = createSelector(
  selectedWorkshopDate,
  (selectedDate) => selectedDate?.timeSlots ?? []
);
export const selectSelectedDropoffOptionId = createSelector(
  selectBookingState,
  (state: BookingState) => state.selectedDropoffOptionId
);
export const selectDropoffOptions = createSelector(
  selectBasePackages,
  selectedWorkshopDate,
  (basePackages, selectedDate) => {
    if (!basePackages || !selectedDate) return [];
    return basePackages.filter(
      (p) =>
        p.type === PackageTypeEnum.DropoffOption &&
        selectedDate.availableDropoffoptionIds.includes(p.id)
    );
  }
);
export const selectSelectedDropoffPackage = createSelector(
  selectDropoffOptions,
  selectSelectedDropoffOptionId,
  (options, selectedId) => options.find((o) => o.id === selectedId) ?? null
);

export const selectIsEbuEnabled = createSelector(
  selectSelectedDropoffPackage,
  (dropoffPackage) =>
    !!dropoffPackage?.subPackages?.some((sp) =>
      sp.properties?.includes(PropertyEnum.EuropcarDropoff)
    )
);

export const selectWorkshopHoursForSelectedDate = createSelector(
  selectSelectedDealer,
  selectSelectedDate,
  (dealer, selectedDate) => _getWorkshopHoursForDate(dealer, selectedDate)
);

export const selectEbuCars = createSelector(
  selectBookingState,
  (state: BookingState) => state.ebuCars
);

export const selectSelectedEbuCarId = createSelector(
  selectBookingState,
  (state: BookingState) => state.selectedEbuCarId
);

export const selectSelectedEbuCar = createSelector(
  selectEbuCars,
  selectSelectedEbuCarId,
  (cars, selectedId) => cars?.find((car) => car.groupID === selectedId) ?? null
);

export const selectPackagesUsedForTimeRequest = createSelector(
  selectBookingState,
  (state: BookingState) => state.packagesUsedForTimeRequest
);

export const selecTimeSelectionForm = createSelector(
  selectSelectedPackages,
  (form: SelectedPackages) => form.timeSelectionForm || null
);

// --- Step 7  ---
export const selectContactInformation = createSelector(
  selectBookingState,
  (state: BookingState) => state.contactInformation
);

export const selectCampaignResponse = createSelector(
  selectBookingState,
  (state: BookingState) => state.campaignResponse
);

export const selectCampaignValidationState = createSelector(
  selectBookingState,
  (state: BookingState) => state.campaignValidationState
);

// --- Step 8  ---
export const selectOrderId = createSelector(
  selectBookingState,
  (state: BookingState) => state.orderId
);

export const selectBookingError = createSelector(
  selectBookingState,
  (state: BookingState) => state.bookingError
);

export const selectIsBookingMadeSuccessful = createSelector(
  selectBookingState,
  (state: BookingState) => state.isBookingMadeSuccessful
);

// --- Shared (More complex that dependends on other selectors) ---
export const selectAllAvailablePackages = createSelector(
  selectBasePackages,
  selectAdditionalPackages,
  (base, additional) => [...(base ?? []), ...(additional ?? [])]
);
export const selectSelectedBasePackagesForRequest = createSelector(
  selectBasePackages,
  selectSelectedPackages,
  selectSelectedWorkshopSystems,
  (
    basePackages: PackageResponse[] | null,
    selectedPackages: SelectedPackages | null,
    selectedSystems: WorkshopSystemEnum[] | null
  ): SelectedPackagesRequest[] => {
    if (!basePackages || !selectedSystems) {
      return [];
    }

    const selectedBase: SelectedPackagesRequest[] = [];

    selectedSystems.forEach((system) => {
      const formName = systemToFormNameMap[system];
      if (!formName) return;

      const formPart = selectedPackages ? selectedPackages[formName] : null;
      let selectedPackageId: string | null =
        formPart?.selectedContainer || null;

      if (!selectedPackageId) {
        let relevantPackages: PackageResponse[];
        if (system === WorkshopSystemEnum.Service) {
          relevantPackages = basePackages.filter((p) =>
            [
              PackageTypeEnum.OriginalService,
              PackageTypeEnum.BaseService
            ].includes(p.type)
          );
        } else {
          const type = systemToPackageTypeMap[system];
          relevantPackages = type
            ? basePackages.filter((p) => p.type === type)
            : [];
        }
        const recommendedPackage = relevantPackages.find((p) =>
          p.tags.includes(PackageTagEnum.Recommended)
        );
        if (recommendedPackage) {
          selectedPackageId = recommendedPackage.id;
        }
      }

      if (selectedPackageId) {
        const fullPackageInfo = basePackages.find(
          (p) => p.id === selectedPackageId
        );
        if (fullPackageInfo) {
          let subPackageIds: string[] = [];
          const packageSelections = formPart
            ? formPart[selectedPackageId]
            : null;

          if (fullPackageInfo.isSubCategory) {
            const recommendedSub = fullPackageInfo.subPackages.find((sp) =>
              sp.tags.includes(PackageTagEnum.Recommended)
            );
            subPackageIds = [
              (packageSelections as any)?.subcategoryContainer ||
                recommendedSub?.id
            ].filter(Boolean);
          } else {
            if (packageSelections && typeof packageSelections === "object") {
              subPackageIds = Object.keys(packageSelections).filter(
                (key) =>
                  packageSelections[key] === true && key !== "selectedContainer"
              );
            } else {
              subPackageIds = fullPackageInfo.subPackages
                .filter(
                  (sp) =>
                    sp.tags.includes(PackageTagEnum.Recommended) ||
                    sp.tags.includes(PackageTagEnum.MainPackage)
                )
                .map((sp) => sp.id);
            }
          }
          selectedBase.push(
            new SelectedPackagesRequest({
              packageId: +fullPackageInfo.id,
              subPackageIds
            })
          );
        }
      }
    });

    return selectedBase;
  }
);

export const selectSelectedAdditionalPackagesForRequest = createSelector(
  selectAdditionalPackages,
  selectSelectedPackages,
  (
    additionalPackages: PackageResponse[] | null,
    selectedPackages: SelectedPackages | null
  ): SelectedPackagesRequest[] => {
    if (!additionalPackages || !selectedPackages?.additionalPackagesForm) {
      return [];
    }

    const selectedAdditional: SelectedPackagesRequest[] = [];
    const additionalFormPart = selectedPackages.additionalPackagesForm;

    additionalPackages.forEach((pkg) => {
      const selection = additionalFormPart[pkg.id];
      if (selection && selection.selected) {
        let subPackageIds: string[] = [];
        if (pkg.isSubCategory) {
          subPackageIds = [selection.subcategoryContainer as string];
        } else {
          subPackageIds = Object.keys(selection).filter(
            (key) => key !== "selected" && selection[key] === true
          );
        }

        const numericPackageId = +pkg.id;
        if (numericPackageId) {
          selectedAdditional.push(
            new SelectedPackagesRequest({
              packageId: numericPackageId,
              subPackageIds: subPackageIds.filter((id) => !!id)
            })
          );
        } else {
          selectedAdditional.push(
            new SelectedPackagesRequest({
              packageExternalId: pkg.packageExternalId || pkg.id,
              subPackageIds: subPackageIds.filter((id) => !!id)
            })
          );
        }
      }
    });

    return selectedAdditional;
  }
);

export const selectSelectedTimePackagesForRequest = createSelector(
  selectBasePackages,
  selectSelectedPackages,
  (basePackages, selectedPackages): SelectedPackagesRequest[] => {
    if (!basePackages || !selectedPackages?.timeSelectionForm) {
      return [];
    }
    const timeSelectionFormValue = selectedPackages.timeSelectionForm;
    const selectedContainerId = timeSelectionFormValue.selectedContainer;

    if (!selectedContainerId) {
      return [];
    }

    const selectedPackage = basePackages.find(
      (p) => p.id === selectedContainerId
    );
    if (!selectedPackage) {
      return [];
    }

    let subPackageIds: string[] = [];
    const subPackageSelections = timeSelectionFormValue[selectedContainerId];

    if (selectedPackage.isSubCategory) {
      if (
        subPackageSelections &&
        typeof subPackageSelections === "object" &&
        "subcategoryContainer" in subPackageSelections
      ) {
        const subId = subPackageSelections.subcategoryContainer as string;
        if (subId) subPackageIds = [subId];
      }
    } else {
      if (subPackageSelections && typeof subPackageSelections === "object") {
        subPackageIds = Object.keys(subPackageSelections).filter(
          (key) => subPackageSelections[key] === true
        );
      }
    }

    return [
      new SelectedPackagesRequest({
        packageId: +selectedPackage.id,
        subPackageIds: subPackageIds
      })
    ];
  }
);
export const selectAllSelectedPackagesForRequest = createSelector(
  selectSelectedBasePackagesForRequest,
  selectSelectedAdditionalPackagesForRequest,
  selectSelectedTimePackagesForRequest,
  (selectedBase, selectedAdditional, selectedTime) => {
    return [...selectedBase, ...selectedAdditional, ...selectedTime];
  }
);

export const selectAreAllRequiredPackagesSelected = createSelector(
  selectSelectedWorkshopSystems,
  selectSelectedPackages,
  selectBasePackages,
  (selectedSystems, selectedPackages, basePackages): boolean => {
    if (!selectedSystems || !selectedPackages) {
      return false;
    }

    if (
      selectedSystems.length === 1 &&
      selectedSystems[0] === WorkshopSystemEnum.Other
    ) {
      return (selectedPackages?.other?.length ?? 0) >= requiredCharacters;
    }

    return selectedSystems.every((system) => {
      if (system === WorkshopSystemEnum.Other) {
        return true;
      }

      const formName = systemToFormNameMap[system];
      if (!formName) {
        return true;
      }

      if (system === WorkshopSystemEnum.Service) {
        const servicePackagesAvailable = (basePackages ?? []).some((p) =>
          [
            PackageTypeEnum.OriginalService,
            PackageTypeEnum.BaseService
          ].includes(p.type)
        );
        if (!servicePackagesAvailable) {
          return (selectedPackages?.other?.length ?? 0) >= requiredCharacters;
        }
      }

      const formPart = selectedPackages[formName];
      const isPackageSelected = !!formPart?.selectedContainer;

      if (system === WorkshopSystemEnum.Windscreen) {
        const windscreenFormPart = formPart;
        const hasInsurance = !!windscreenFormPart?.insurance;
        const hasDate = !!windscreenFormPart?.date;
        return isPackageSelected && hasInsurance && hasDate;
      }

      if (system === WorkshopSystemEnum.Troubleshooting) {
        const troubleshootingFormPart = formPart;
        const hasDescription = !!troubleshootingFormPart?.helpText;
        return isPackageSelected && hasDescription;
      }

      return isPackageSelected;
    });
  }
);
